<script setup lang="ts">
import type { AgentSearchResult } from '~~/core/entities/AgentSearch';
import type { ClassValue } from 'clsx';

import { BrokerAvatar } from '@/components/BrokerAvatar';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/utils';

interface Props {
	isFetchingForTheFirstTime: boolean;
	hasProperties: boolean;
	propertyCountLabel: string;
	infoLabel: string | null;
	selectedAgent?: AgentSearchResult;
	isFetchingSelectedAgent?: boolean;
	countLabelClasses?: ClassValue;
	propertyCountLabelClasses?: ClassValue;
}

const props = withDefaults(defineProps<Props>(), {
	selectedAgent: undefined,
	isFetchingSelectedAgent: false,
	countLabelClasses: undefined,
	propertyCountLabelClasses: undefined
});
</script>

<template>
	<div
		data-testid="listing-summary"
		class="flex w-full items-end justify-between pb-3 transition-all duration-300 ease-out lg:pb-5"
	>
		<div
			v-if="isFetchingForTheFirstTime"
			data-testid="listing-summary-initial-loading"
			class="flex w-full items-center justify-between"
		>
			<div class="w-[70%] space-y-1">
				<Skeleton is-active variant="white" class="h-[28px] w-[100px]" />
				<Skeleton is-active variant="white" class="h-[21px] w-[200px]" />
			</div>
			<Skeleton
				data-testid="listing-summary-sort-loading"
				is-active
				variant="white"
				class="h-[40px] w-[120px]"
			/>
		</div>

		<div
			v-else-if="hasProperties"
			class="flex w-full min-w-0 items-center justify-between"
		>
			<div class="min-w-0 flex-1 text-left">
				<h1 v-if="isFetchingSelectedAgent" class="sr-only">
					{{ propertyCountLabel }} {{ infoLabel }}
				</h1>
				<div
					v-if="isFetchingSelectedAgent"
					data-testid="listing-summary-agent-loading"
					class="flex min-w-0 flex-col gap-0.5"
					role="status"
				>
					<span data-testid="listing-summary-loading-status" class="sr-only">
						Carregando resumo da listagem
					</span>
					<div
						data-testid="listing-summary-title-loading"
						class="h-[27px] w-[240px] max-w-full lg:h-[30px] lg:w-[280px]"
						aria-hidden="true"
					>
						<Skeleton is-active variant="white" class="size-full" />
					</div>
					<div
						data-testid="listing-summary-location-loading"
						class="h-[18px] w-[140px] max-w-full lg:h-[21px] lg:w-[160px]"
						aria-hidden="true"
					>
						<Skeleton is-active variant="white" class="size-full" />
					</div>
				</div>

				<h1
					v-else
					data-testid="listing-summary-title"
					:class="
						cn(
							'flex min-w-0 flex-col gap-0.5 font-matter-sq text-lg font-normal leading-normal tracking-[-0.36px] text-primary lg:text-xl lg:tracking-[-0.4px]',
							countLabelClasses
						)
					"
				>
					<span
						v-if="selectedAgent"
						data-testid="listing-summary-agent"
						class="flex min-w-0 items-center gap-2"
						:aria-label="`${propertyCountLabel} de ${selectedAgent.name}`"
					>
						<span class="shrink-0" aria-hidden="true">
							{{ propertyCountLabel }} de
						</span>
						<span class="flex min-w-0 items-center gap-1" aria-hidden="true">
							<BrokerAvatar
								aria-hidden="true"
								class="flex size-4 shrink-0 lg:size-5"
								:broker-src="selectedAgent.profilePicture.url"
								:broker-name="selectedAgent.name"
								:company-name="selectedAgent.company?.name || ''"
								:show-company-avatar="false"
							/>
							<span
								data-testid="listing-summary-agent-name"
								class="min-w-0 truncate"
							>
								{{ selectedAgent.name }}
							</span>
						</span>
					</span>

					<span v-else>{{ propertyCountLabel }}</span>

					<span
						data-testid="listing-summary-location"
						:class="
							cn(
								'font-sans text-xs font-normal leading-normal tracking-normal text-primary lg:text-sm',
								propertyCountLabelClasses
							)
						"
					>
						{{ infoLabel }}
					</span>
				</h1>
			</div>

			<div class="flex shrink-0 items-center gap-1 xl:gap-[0.63rem]">
				<slot name="actions" />
			</div>
		</div>
	</div>
</template>
